/** @jsxImportSource @emotion/react */
import * as s from './style';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Stroke, Fill } from 'ol/style';
import { GEOSERVER } from 'config/URL/URL';
import LifeStylePopup from '../../popups/LifeStylePopup';
import TileWMS from 'ol/source/TileWMS';
import useBaseOlMap from 'hooks/useBaseOlMap';
import useMapOverlay from 'hooks/map/useMapOverlay';
import useFeatureSelection from 'hooks/map/useFeatureSelection';
import useDynamicVectorLayers from 'hooks/map/useDynamicVectorLayers';
import useLifestylePoints from 'hooks/map/useLifestylePoints';
import useViewportParams from 'hooks/map/useViewportParams';
import useLifestyleRadiusSelection from 'hooks/map/useLifestyleRadiusSelection';
import { fetchAdmAreaInfo, fetchLifestyleRadiusInfo } from 'services/lab2/lifestyleService';
import { createOsmBaseLayer } from 'utils/map/baseLayers';
import { getLayerNameByZoom } from 'utils/map/zoomUtils';
import { buildLifestyleSymbol } from 'utils/lab2/symbolBuilders';
import { Feature } from 'ol';
import { Circle } from 'ol/geom';

const HOSPITAL_KEYS = [
    { prop: 'h_cnt_01_11', target: 'hCnt_01_11' },
    { prop: 'h_cnt_21_31', target: 'hCnt_21_31' },
    { prop: 'h_cnt_41_51', target: 'hCnt_41_51' },
    { prop: 'h_cnt_61', target: 'hCnt_61' },
    { prop: 'h_cnt_71_72_73_75', target: 'hCnt_71_72_73_75' },
    { prop: 'h_cnt_81', target: 'hCnt_81' },
    { prop: 'h_cnt_92_93', target: 'hCnt_92_93' },
];

const EDUCATION_KEYS = [
    { prop: 'e_cnt_01', target: 'eCnt_01' },
    { prop: 'e_cnt_02', target: 'eCnt_02' },
    { prop: 'e_cnt_03', target: 'eCnt_03' },
    { prop: 'e_cnt_04_05_06', target: 'eCnt_04_05_06' },
    { prop: 'e_cnt_07', target: 'eCnt_07' },
    { prop: 'e_cnt_08', target: 'eCnt_08' },
    { prop: 'e_cnt_09', target: 'eCnt_09' },
    { prop: 'e_cnt_10', target: 'eCnt_10' },
];

const sumValues = (items, prop) =>
    items.reduce((acc, item) => acc + Number(item?.[prop] ?? 0), 0);

const extractPropertiesList = (data) => {
    if (!data) return [];
    if (Array.isArray(data?.features)) {
        return data.features.map((feature) => feature?.properties ?? {});
    }
    if (Array.isArray(data)) {
        return data;
    }
    if (Array.isArray(data?.records)) {
        return data.records;
    }
    return [data];
};

const buildRadiusSelection = (propsList, type, label) => {
    const selection =
        type === 'hos'
            ? HOSPITAL_KEYS.reduce(
                  (acc, { prop, target }) => ({
                      ...acc,
                      [target]: sumValues(propsList, prop),
                  }),
                  {}
              )
            : EDUCATION_KEYS.reduce(
                  (acc, { prop, target }) => ({
                      ...acc,
                      [target]: sumValues(propsList, prop),
                  }),
                  {}
              );

    return {
        hjdNm: label,
        ...selection,
    };
};

const LifestyleMap = ({
    isExpanded,
    isMapAreaSelectMode,
    selectedLocation, 
    setSelectedLocation,
    setIsMapAreaSelectMode,
    radius,
    type,
    admCd,
}) => {
    const baseLayerFactory = useCallback(() => createOsmBaseLayer({ title: 'OSM Base Map' }), []);

    const { mapRef, initializeMap, isMapReady } = useBaseOlMap({
        targetId: 'lifestyle_map_area',
        initialCenter: [1.416517405965338e7, 4294546.35698363],
        initialZoom: 7.8,
        minZoom: 7,
        maxZoom: 18,
        baseLayerFactory,
    });
    const lifestyleOverlayOptions = useMemo(() => ({ autoPan: false }), []);
    const overlayRef = useMapOverlay({
        mapRef,
        elementId: 'lifestyle_popup',
        isMapReady,
        options: lifestyleOverlayOptions,
    });
    const borderLayerRef = useRef(null);
    const wmsLayerRef = useRef(null);

    const [lifeStylePopup, setLifeStylePopup] = useState('hos');
    const { syncLayers: syncLifestyleLayers } = useDynamicVectorLayers({ mapRef });
    const viewportParams = useViewportParams({
        mapRef,
        isMapReady,
        resolveLayerName: getLayerNameByZoom,
    });
    const layerName = viewportParams?.layerName ?? null;
    const getPointSVG = useCallback(
        (feature) => buildLifestyleSymbol(feature, { layerName, type }),
        [layerName, type]
    );

    const handleRadiusSelectionComplete = useCallback(
        async ({ coordinate, radius: selectedRadius }) => {
            if (!type) return;
            try {
                const radiusData = await fetchLifestyleRadiusInfo({
                    coordinate,
                    radius: selectedRadius,
                    layerName: type,
                });

                const propsList = extractPropertiesList(radiusData);
                const selection = buildRadiusSelection(
                    propsList,
                    type,
                    `${Math.round(selectedRadius)}m 반경 검색`
                );

                setSelectedLocation({...selection, coord: coordinate, mode: 'radius'});

                const circleFeature = new Feature({
                    geometry: new Circle(coordinate, selectedRadius),
                })

                const circleLayer = new VectorLayer({
                    source: new VectorSource({
                        features: [circleFeature],
                    }),
                    style: new Style({
                        stroke: new Stroke({ color: '#ff9800', width: 2 }),
                        fill: new Fill({ color: 'rgba(255, 152, 0, 0.15)' }),
                    }),
                    layerName: 'circleLayer'
                });

                const pastLayer = mapRef.current.getLayers().getArray().find((layer) => layer.get("layerName") === "circleLayer");

                mapRef.current.removeLayer(pastLayer);
                mapRef.current.addLayer(circleLayer);
            } catch (error) {
                console.error('Failed to fetch lifestyle radius info', error);
            } finally {
                setIsMapAreaSelectMode?.(false);
            }
        },
        [layerName, overlayRef, setIsMapAreaSelectMode, type]
    );

    useLifestyleRadiusSelection({
        mapRef,
        radius,
        isActive: Boolean(isMapAreaSelectMode && type),
        onComplete: handleRadiusSelectionComplete,
        onDeactivate: () => {
            setIsMapAreaSelectMode?.(false);
        },
    });

    useLifestylePoints({
        mapRef,
        type,
        params: viewportParams,
        isMapReady,
        syncLayers: syncLifestyleLayers,
        getPointSVG,
        selectedLocation,
        setSelectedLocation
    });

    const ensureBorderLayer = useCallback(() => {
        const map = mapRef.current;
        if (!map) return null;

        if (!borderLayerRef.current) {
            const layer = new VectorLayer({
                source: new VectorSource(),
                style: new Style({
                    stroke: new Stroke({ color: '#ff9800', width: 2 }),
                    fill: new Fill({ color: 'rgba(255, 152, 0, 0.15)' }),
                }),
                zIndex: 900,
            });
            layer.set('name', 'lifestyle-border-layer');
            borderLayerRef.current = layer;
            map.addLayer(layer);

        }

        return borderLayerRef.current;
    }, [mapRef]);

    const fetchBorderLayer = useCallback(async () => {
        const map = mapRef.current;
        if (!map) return;

        if (!admCd) {
            borderLayerRef.current?.getSource()?.clear();
            return;
        }

        try {
            const data = await fetchAdmAreaInfo(admCd);
            const geojson = data?.geojson ?? data;
            if (!geojson) return;

            const borderLayer = ensureBorderLayer();
            if (!borderLayer) return;

            const source = borderLayer.getSource();
            source.clear();

            const features = new GeoJSON().readFeatures(geojson, {
                featureProjection: map.getView().getProjection(),
            });
            if (!features.length) return;

            source.addFeatures(features);
            const extent = source.getExtent();
            if (extent && extent.every((value) => Number.isFinite(value))) {
                map.getView().fit(extent, {
                    duration: 500,
                    padding: [40, 40, 40, 40],
                    maxZoom: 12,
                });
            }
        } catch (error) {
            console.error('Failed to fetch lifestyle border layer', error);
        }
    }, [admCd, ensureBorderLayer, mapRef]);

    useEffect(() => {
        initializeMap();
    }, [initializeMap]);

    useEffect(() => {
        if(selectedLocation?.hjdNm) {
            overlayRef.current?.setPosition(selectedLocation?.coord);
        }
        
        if(!selectedLocation || selectedLocation?.mode !== 'radius') {
            const pastLayer = mapRef.current.getLayers().getArray().find((layer) => layer.get("layerName") === "circleLayer");
            mapRef.current.removeLayer(pastLayer);
        }

        return () => {
            overlayRef.current?.setPosition(undefined);
        }

    }, [selectedLocation])

    useEffect(() => {
        if(selectedLocation?.mode !== 'radius') {
            setSelectedLocation(undefined);
        }
    }, [layerName])

    useEffect(() => {
        setSelectedLocation(undefined);
    }, [type])

    const lifestyleFeatureToSelection = useCallback(
        (feature) => {
            if (!feature || !type) return null;
            if (type === 'hos') {
                return {
                    hjdCd: feature.get('admin_cd'),
                    hjdNm: feature.get('admin_nm'),
                    hCnt_01_11: feature.get('h_cnt_01_11'),
                    hCnt_21_31: feature.get('h_cnt_21_31'),
                    hCnt_28: feature.get('h_cnt_28'),
                    hCnt_41_51: feature.get('h_cnt_41_51'),
                    hCnt_61: feature.get('h_cnt_61'),
                    hCnt_71_72_73_75: feature.get('h_cnt_71_72_73_75'),
                    hCnt_81: feature.get('h_cnt_81'),
                    hCnt_92_93: feature.get('h_cnt_92_93'),
                    feature,
                };
            }
            return {
                hjdCd: feature.get('admin_cd'),
                hjdNm: feature.get('admin_nm'),
                eCnt_01: feature.get('e_cnt_01'),
                eCnt_02: feature.get('e_cnt_02'),
                eCnt_03: feature.get('e_cnt_03'),
                eCnt_04_05_06: feature.get('e_cnt_04_05_06'),
                eCnt_07: feature.get('e_cnt_07'),
                eCnt_08: feature.get('e_cnt_08'),
                eCnt_09: feature.get('e_cnt_09'),
                eCnt_10: feature.get('e_cnt_10'),
                feature,
            };
        },
        [type]
    );

    const handleLifestyleFeatureClick = useFeatureSelection({
        mapRef,
        overlayRef,
        featureToSelection: lifestyleFeatureToSelection,
        onSelectionChange: (selection, feature, coord) => {
            setSelectedLocation({...selection, coord} ?? null);
        },
        hitTolerance: 12,
    });

    useEffect(() => {
        if (type) {
            setLifeStylePopup(type);
            setSelectedLocation(undefined);
        } else {
            setSelectedLocation(null);
            syncLifestyleLayers([]);
        }
    }, [syncLifestyleLayers, type]);

    useEffect(() => {
        if (!isMapReady) return;
        const map = mapRef.current;
        if (!map) return;

        if (!wmsLayerRef.current) {
            const wmsLayer = new TileLayer({
                source: new TileWMS({
                    url: GEOSERVER + '/geoserver/ows',
                    params: {
                        LAYERS: 'tb_adm_sido_polygon',
                        TILED: true,
                        VERSION: '1.1.1',
                        FORMAT: 'image/png',
                        TRANSPARENT: true,
                    },
                    serverType: 'geoserver',
                    transition: 0,
                }),
                visible: true,
            });
            wmsLayerRef.current = wmsLayer;
            map.addLayer(wmsLayer);
        }
    }, [isMapReady, mapRef]);

    useEffect(() => {
        if (!isMapReady || !mapRef.current) return;
        const timeoutId = setTimeout(() => {
            mapRef.current?.updateSize();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [isExpanded, isMapReady]);

    useEffect(() => {
        if (!isMapReady) return;
        fetchBorderLayer();
    }, [admCd, fetchBorderLayer, isMapReady]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) return;
        map.on('click', handleLifestyleFeatureClick);
        return () => {
            map.un('click', handleLifestyleFeatureClick);
        };
    }, [handleLifestyleFeatureClick, isMapReady, mapRef]);

    const handlePopupClose = () => {
        setSelectedLocation(undefined);
    };


    return (
        <div css={s.mainContainer}>
            <div id="lifestyle_map_area" css={s.mapArea}></div>


            {/* 인구현황 Popup */}
            <LifeStylePopup 
                selectedLocation={selectedLocation}
                handlePopupClose={handlePopupClose}
                lifeStylePopup={lifeStylePopup}
            />
        </div>
    );
};

export default LifestyleMap;

